import { lazy, Suspense } from 'react';

import { Navigate, Outlet, type RouteObject } from 'react-router-dom';

import { SplashScreen } from '@tankef/ui';

import { GuestRoute } from '../guards/GuestRoute';
import { TermsRoute } from '../guards/TermsRoute';

import { APPLICANT_KYC_VARIANT } from '@/domains/identity/onboarding/routes/steps/kycVariant';

const LoginPage = lazy(() =>
  import('@/domains/identity/auth/routes').then(m => ({ default: m.LoginPage }))
);
// Plan 07 F4 C.c.visual — RegisterPage se monta directamente en los URLs
// de registration + kyc-capture para preservar el layout de 5 cards. El
// contrato visual de 5-cards no era compatible con "una página por step"
// así que regresamos al layout original con URLs distintas por card activa.
// F7 borró los step components Email/Phone/etc. que F2 había creado
// asumiendo el split — quedaron como dead code tras la decisión de C.c.visual.
const RegisterPage = lazy(() =>
  import('@/domains/identity/auth/routes').then(m => ({ default: m.RegisterPage }))
);
const PartyTypeSelectorPage = lazy(() =>
  import('@/domains/identity/auth/routes').then(m => ({ default: m.PartyTypeSelectorPage }))
);
const ActivateAccountPage = lazy(() =>
  import('@/domains/identity/auth/routes').then(m => ({ default: m.ActivateAccountPage }))
);
const ForgotPasswordPage = lazy(() =>
  import('@/domains/identity/auth/routes').then(m => ({ default: m.ForgotPasswordPage }))
);
const IneCaptureStep = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.IneCaptureStep }))
);
const SelfieStep = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.SelfieStep }))
);
const KycStepDoneGuard = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.KycStepDoneGuard }))
);
const IdentityResultStep = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.IdentityResultStep }))
);
const OnboardingShell = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.OnboardingShell }))
);
const OnboardingEntry = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.OnboardingEntry }))
);
const OnboardingContinuationGate = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.OnboardingContinuationGate,
  }))
);
const PersonalDataStep = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.PersonalDataStep }))
);
const AddressStep = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.AddressStep }))
);
const CurpStep = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.CurpStep }))
);
const RfcStep = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.RfcStep }))
);
const TransactionalProfileStep = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.TransactionalProfileStep,
  }))
);
const BeneficiaryStep = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.BeneficiaryStep }))
);
const ContractStep = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.ContractStep }))
);
// FiscalDocumentStep se rutea dentro del Shell (flujo alterno de RFC).
const FiscalDocumentStep = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.FiscalDocumentStep }))
);
const RecoveryPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.RecoveryPage }))
);
const MoralShell = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.MoralShell }))
);
const MoralAccessPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.MoralAccessPage }))
);
const MoralCompanyDocsPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralCompanyDocsPage,
  }))
);
const MoralCompanyStructurePage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralCompanyStructurePage,
  }))
);
const MoralCompanyOrgStructurePage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralCompanyOrgStructurePage,
  }))
);
const MoralCompanyDataPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralCompanyDataPage,
  }))
);
const MoralCompanyAddressPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralCompanyAddressPage,
  }))
);
const MoralBeneficialOwnerIdentityPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralBeneficialOwnerIdentityPage,
  }))
);
const MoralCompanyScianPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralCompanyScianPage,
  }))
);
const MoralOriginOfFundsPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralOriginOfFundsPage,
  }))
);
const MoralLegalRepConsentPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralLegalRepConsentPage,
  }))
);
const MoralLegalRepDataPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralLegalRepDataPage,
  }))
);
const MoralLegalRepAddressPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralLegalRepAddressPage,
  }))
);
const MoralLegalRepIneCapturePage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralLegalRepIneCapturePage,
  }))
);
const MoralLegalRepSelfiePage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralLegalRepSelfiePage,
  }))
);
const MoralPepDeclarationPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralPepDeclarationPage,
  }))
);
const MoralLegalReviewWaitPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralLegalReviewWaitPage,
  }))
);
const MoralPrivacyNoticePage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralPrivacyNoticePage,
  }))
);
const MoralContractsPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralContractsPage,
  }))
);
const MoralFinalizePage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({
    default: m.MoralFinalizePage,
  }))
);
const WelcomePage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.WelcomePage }))
);

export function identityRoutes(): RouteObject[] {
  return [
    {
      element: <GuestRoute />,
      children: [
        {
          element: (
            <Suspense fallback={<SplashScreen />}>
              <Outlet />
            </Suspense>
          ),
          children: [
            { path: '/login', element: <LoginPage /> },
            // Plan 07 F4 C.c — `/register` se vuelve redirect al Shell.
            // OnboardingEntry maneja el bootstrap de sesión cuando el
            // usuario aún no tiene token (creando una nueva via
            // useCreateOnboardingSession).
            //
            // C.e movió el guard de Términos y Condiciones (antes
            // TermsRoute aquí) dentro de OnboardingEntry, donde sólo
            // se evalúa cuando estamos a punto de crear sesión. Para
            // usuarios mid-flow (con token válido) Entry se salta el
            // gate — ya aceptaron T&C para tener sesión en primer lugar.
            // Plan 09 namespace refactor (2026-05-20): URLs splittadas por
            // componente que rendea —
            //   /register/*  → RegisterPage (5-cards layout) — registration
            //                  + kyc-capture steps
            //   /kyc/*       → IneCaptureStep + SelfieStep + IdentityResultStep
            //   /onboarding/*→ OnboardingShell (Entry + data/address/curp/etc)
            //                  + RecoveryPage
            //
            // Legacy redirects mantienen compat para deep-links en emails
            // enviados antes del refactor. Plan 08 F2 puede retirarlos tras
            // telemetry snapshot.

            // /register sin slug → selector PF/PM (F2 plan Persona Moral). Tras
            // elegir, el selector setea partyType y navega a /onboarding (Entry
            // crea la sesión con el kind correcto).
            { path: '/register', element: <Navigate to="/register/party-type" replace /> },
            { path: '/register/party-type', element: <PartyTypeSelectorPage /> },

            // RegisterPage URLs (registration + kyc-capture, 5-cards layout).
            {
              path: '/register',
              children: [
                {
                  path: 'email',
                  element: (
                    <TermsRoute>
                      <RegisterPage />
                    </TermsRoute>
                  ),
                },
                {
                  path: 'email-verification',
                  element: (
                    <TermsRoute>
                      <RegisterPage />
                    </TermsRoute>
                  ),
                },
                {
                  path: 'phone',
                  element: (
                    <TermsRoute>
                      <RegisterPage />
                    </TermsRoute>
                  ),
                },
                {
                  path: 'phone-verification',
                  element: (
                    <TermsRoute>
                      <RegisterPage />
                    </TermsRoute>
                  ),
                },
                {
                  path: 'password',
                  element: (
                    <TermsRoute>
                      <RegisterPage />
                    </TermsRoute>
                  ),
                },
                {
                  path: 'location-and-camera',
                  element: (
                    <TermsRoute>
                      <RegisterPage />
                    </TermsRoute>
                  ),
                },
                // 2026-05-25: URL dedicada para el card "Tu identidad" (la
                // autorización de validar identidad, último step de register
                // antes del flow KYC). Antes este card vivía como overlay de
                // state sobre /register/password (auto-active cuando
                // passwordSet=true). Tener URL propia permite back-nav
                // sequence-aware: /kyc/ine-capture → VOLVER → /register/identity.
                // Forward flow legacy sigue usando /register/password's
                // auto-activation hasta sub-commit 2.
                {
                  path: 'identity',
                  element: (
                    <TermsRoute>
                      <RegisterPage />
                    </TermsRoute>
                  ),
                },
                // Plan 09 Sprint A (2026-05-20): /register/ine-capture y
                // /register/selfie removidos — la captura KYC vive ahora bajo
                // /kyc/*. RegisterPage's last URL es /register/password con el
                // card "Tu identidad" activo (botón VALIDAR IDENTIDAD navega
                // a /kyc/ine-capture).
                // Redirects legacy preservan compat para deep-links viejos.
                {
                  path: 'ine-capture',
                  element: <Navigate to="/kyc/ine-capture" replace />,
                },
                { path: 'selfie', element: <Navigate to="/kyc/selfie" replace /> },
              ],
            },

            // /onboarding/recover stays as a sibling of /onboarding so the parent's
            // catch-all child route never swallows it. Queda FUERA de la compuerta
            // de continuación: es la reanudación por OTP y no depende de /me.
            { path: '/onboarding/recover', element: <RecoveryPage /> },

            // RFC 0030 §12.2: continuación con cuenta activada. La compuerta
            // adopta la sesión de /me antes de que Entry o cualquier paso PM/KYC
            // pidan un session_id que el cliente no tenía. Sin sesión local ni
            // cuenta, rinde de inmediato (registro anónimo intacto).
            {
              element: <OnboardingContinuationGate />,
              children: [
                // KYC URLs (plan 09 F2 shipped): cada sub-step tiene su propio
                // componente. `verification` queda como redirect a ine-capture
                // (entrypoint viejo de URL unificada — ya no aplica).
                {
                  path: '/kyc',
                  children: [
                    { path: 'verification', element: <Navigate to="/kyc/ine-capture" replace /> },
                    {
                      // RFC 0030 §13.2: con INE y selfie ya hechos no se vuelve a capturar.
                      path: 'ine-capture',
                      element: (
                        <KycStepDoneGuard variant={APPLICANT_KYC_VARIANT}>
                          <IneCaptureStep />
                        </KycStepDoneGuard>
                      ),
                    },
                    {
                      path: 'selfie',
                      element: (
                        <KycStepDoneGuard variant={APPLICANT_KYC_VARIANT}>
                          <SelfieStep />
                        </KycStepDoneGuard>
                      ),
                    },
                    { path: 'identity-result', element: <IdentityResultStep /> },
                  ],
                },

                // PM (F3.3 / F4.2 / F4.3): bloques account_access + company_docs +
                // company_data (standalone, fuera del Shell). Gateados por el flag
                // dentro de cada página (redirigen al selector si está off).
                // PM steps form-style bajo MoralShell: chrome unificado (logo +
                // CANCELAR + ancho), beforeunload y flag-gate centralizados. Las 4
                // rutas de access renderizan MoralAccessPage (el card stack deriva
                // la card activa de la URL — paridad de segmentación con PF).
                {
                  element: <MoralShell />,
                  children: [
                    { path: '/onboarding/access', element: <MoralAccessPage /> },
                    { path: '/onboarding/access/email', element: <MoralAccessPage /> },
                    { path: '/onboarding/access/email-verification', element: <MoralAccessPage /> },
                    { path: '/onboarding/access/phone', element: <MoralAccessPage /> },
                    { path: '/onboarding/access/phone-verification', element: <MoralAccessPage /> },
                    { path: '/onboarding/access/password', element: <MoralAccessPage /> },
                    { path: '/onboarding/company-docs', element: <MoralCompanyDocsPage /> },
                    {
                      path: '/onboarding/company-structure',
                      element: <MoralCompanyStructurePage />,
                    },
                    {
                      path: '/onboarding/company-org-structure',
                      element: <MoralCompanyOrgStructurePage />,
                    },
                    { path: '/onboarding/company-data', element: <MoralCompanyDataPage /> },
                    { path: '/onboarding/company-address', element: <MoralCompanyAddressPage /> },
                    {
                      path: '/onboarding/propietario-real',
                      element: <MoralBeneficialOwnerIdentityPage />,
                    },
                    { path: '/onboarding/company-scian', element: <MoralCompanyScianPage /> },
                    {
                      path: '/onboarding/legal-rep-consent',
                      element: <MoralLegalRepConsentPage />,
                    },
                    { path: '/onboarding/legal-rep-data', element: <MoralLegalRepDataPage /> },
                    {
                      path: '/onboarding/legal-rep-address',
                      element: <MoralLegalRepAddressPage />,
                    },
                    { path: '/onboarding/origin-of-funds', element: <MoralOriginOfFundsPage /> },
                    { path: '/onboarding/pep-declaration', element: <MoralPepDeclarationPage /> },
                    { path: '/onboarding/privacy-notice', element: <MoralPrivacyNoticePage /> },
                  ],
                },

                // PM steps auto-chromed / terminales: conservan su propio chrome y
                // flag-gate (no van bajo MoralShell). KYC del rep (PageHeader +
                // FlowNavigation propios vía variant), firma (ContractPage), espera
                // KYB y cierre CLABE.
                {
                  path: '/onboarding/legal-rep-ine-front',
                  element: <MoralLegalRepIneCapturePage />,
                },
                {
                  path: '/onboarding/legal-rep-selfie',
                  element: <MoralLegalRepSelfiePage />,
                },
                { path: '/onboarding/legal-review-wait', element: <MoralLegalReviewWaitPage /> },
                { path: '/onboarding/contracts', element: <MoralContractsPage /> },
                { path: '/onboarding/finalize', element: <MoralFinalizePage /> },

                // Onboarding: Entry + Shell steps (data/address/curp/rfc/profile/closing).
                // Los URLs de registration + kyc-capture (email/phone/password/etc.)
                // se movieron a /register/* y /kyc/* arriba.
                {
                  path: '/onboarding',
                  children: [
                    // Index: Entry resuelve bootstrap de sesión + next_step.
                    // No bajo Shell — Entry sólo redirige, no necesita chrome.
                    { index: true, element: <OnboardingEntry /> },

                    // Legacy redirects (plan 09 namespace refactor 2026-05-20):
                    // URLs viejas /onboarding/{registration-or-kyc-capture-slug}
                    // redirigen a /register/{slug} para preservar deep-links de
                    // emails enviados. Retirar tras telemetry snapshot (plan 08 F2).
                    { path: 'email', element: <Navigate to="/register/email" replace /> },
                    {
                      path: 'email-verification',
                      element: <Navigate to="/register/email-verification" replace />,
                    },
                    { path: 'phone', element: <Navigate to="/register/phone" replace /> },
                    {
                      path: 'phone-verification',
                      element: <Navigate to="/register/phone-verification" replace />,
                    },
                    { path: 'password', element: <Navigate to="/register/password" replace /> },
                    {
                      path: 'location-and-camera',
                      element: <Navigate to="/register/location-and-camera" replace />,
                    },
                    {
                      // Plan 09 Sprint A: ahora /kyc/* es el namespace canónico.
                      path: 'ine-capture',
                      element: <Navigate to="/kyc/ine-capture" replace />,
                    },
                    { path: 'selfie', element: <Navigate to="/kyc/selfie" replace /> },

                    // KYC review + profile + closing — bajo el Shell con su chrome.
                    {
                      element: <OnboardingShell />,
                      children: [
                        { path: 'data', element: <PersonalDataStep /> },
                        { path: 'address', element: <AddressStep /> },
                        { path: 'curp', element: <CurpStep /> },
                        { path: 'rfc', element: <RfcStep /> },
                        { path: 'transactional-profile', element: <TransactionalProfileStep /> },
                        { path: 'fiscal-document', element: <FiscalDocumentStep /> },
                        { path: 'beneficiary', element: <BeneficiaryStep /> },
                        { path: 'contract', element: <ContractStep /> },
                        { path: '*', element: <Navigate to="." replace /> },
                      ],
                    },
                  ],
                },
              ],
            },

            // Legacy KYC redirects (plan 09 namespace refactor + Sprint A).
            // /identity-verification y /kyc/verification quedan como bridges
            // al nuevo entrypoint /kyc/ine-capture (sprint A removió la URL
            // unificada — cada sub-step de KYC ahora tiene su propio URL).
            {
              path: '/identity-verification',
              element: <Navigate to="/kyc/ine-capture" replace />,
            },
            {
              path: '/identity-result',
              element: <Navigate to="/kyc/identity-result" replace />,
            },
            { path: '/forgot-password', element: <ForgotPasswordPage /> },
            // RFC 0022 — destino del enlace del correo de invitación.
            { path: '/activar-cuenta', element: <ActivateAccountPage /> },
          ],
        },
      ],
    },
    // /welcome is public — shown right after onboarding for both
    // authenticated (e-sign flow) and unauthenticated users.
    {
      path: '/welcome',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <WelcomePage />
        </Suspense>
      ),
    },
  ];
}
